import React, {useEffect, useMemo, useState} from 'react';
import Input from '../Input';
import FillInput from '../FillOption';
import FillSelect from '../FillSelect';
import {useDispatch, useSelector} from 'react-redux';
import {setFields, setToolScale} from '../../store/builder/reducer';
import {useGetFieldOptionsQuery} from '../../store/builder/inspections.api';
import FillPhoto from '../FillPhoto';
import {toast} from 'react-toastify';
import Tooltip from '../Tooltip';
import FillDryRating from '../FillDryRating';
import FillDate from '../FillDate';
import FillTime from '../FillTime';
import {useTranslation} from '../../hooks/useTranslation';
import {isFlagOn} from '../../utils/flags';
import {describeAcceptable, parseAcceptable} from '../../utils/acceptable';

// Fields carrying `options_url` get their options from that url instead of
// the options stored on the form. The endpoint returns ready made
// `{label, value}` pairs. Answers are always stored as strings, so the values
// are stringified too - otherwise a numeric value would never match the saved
// answer.
const toRemoteOptions = payload => {
  const list = Array.isArray(payload) ? payload : (payload?.data ?? []);
  return list
    .map(entry =>
      entry !== null && typeof entry === 'object'
        ? entry
        : {label: entry, value: entry},
    )
    .filter(
      option =>
        option.value !== null &&
        option.value !== undefined &&
        option.value !== '',
    )
    .map((option, index) => ({
      // Whatever else the endpoint sends is kept: `disabled` greys the option
      // out, and a deice tool carries the scale it reads on in `brix_ri`.
      ...option,
      id: index + 1,
      label: String(option.label ?? option.value),
      value: String(option.value),
      has_comment: false,
    }));
};

// The answer of the field a `get_param_from` entry points at.
const getParamFieldAnswer = (paramFieldId, fields, currentItem) => {
  if (paramFieldId === null || paramFieldId === undefined) {
    return null;
  }
  const paramField =
    fields?.find(field => String(field?.id) === String(paramFieldId)) ??
    currentItem?.fields?.find(
      field => String(field?.id) === String(paramFieldId),
    );
  const answer = paramField?.answers?.[0];
  return answer === null || answer === undefined || answer === ''
    ? null
    : String(answer);
};

const isBlank = value => value === null || value === undefined || value === '';

// `get_param_from` lists the query params of `options_url` as
// `{key, get_from}` entries. `get_from` is either `inspection_id`, for the id
// of the inspection being filled, or the id of the field whose answer is sent.
// A param that cannot be resolved yet comes back as null.
const resolveOptionsParams = (
  paramSources,
  inspectionId,
  fields,
  currentItem,
) =>
  paramSources
    .filter(source => !isBlank(source?.key))
    .map(source => {
      const from = String(source?.get_from ?? '');
      const value =
        from === 'inspection_id'
          ? isBlank(inspectionId)
            ? null
            : String(inspectionId)
          : getParamFieldAnswer(source?.get_from, fields, currentItem);
      return {key: String(source.key), value};
    });

const buildOptionsUrl = (url, params) => {
  if (!url) {
    return null;
  }
  const query = params
    .map(
      ({key, value}) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&');
  if (!query) {
    return url;
  }
  const separator = !url.includes('?')
    ? '?'
    : url.endsWith('?') || url.endsWith('&')
      ? ''
      : '&';
  return `${url}${separator}${query}`;
};

// A field marked `show_acceptable` shows the bound its answer is judged
// against. Some bounds are only known once another answer exists - the fluid
// brand carries the band for each reading, the freeze point sets the LOUT
// floor - and those are worked out into `acceptable` as the answers come in,
// so there is one place to read the bound from either way.

// The backend ships the translations, so a key it does not carry would render
// as the raw key. These are the fallbacks for the acceptable label.
const ACCEPTABLE_LABEL_KEY = 'fillInspection.acceptable';
const ACCEPTABLE_LABEL_FALLBACK = {
  en: 'Acceptable',
  fr: 'Acceptable',
};

export default function FIllField({
  item: fieldItem,
  readOnly: readOnlyProp = false,
  sheet,
  inspection_id,
  currentItem,
  setCurrentItem,
  data,
  calculateDisabled = false,
  correctiveActionsModal,
}) {
  const {t, language} = useTranslation();
  const dispatch = useDispatch();
  const [radioOptions, setRadioOptions] = useState([]);
  const fields = useSelector(state => state.rootReducer.reducer.fields);
  const optionsBaseUrl = fieldItem?.options_url;
  const valueBaseUrl = fieldItem?.value_url;
  const paramSources = fieldItem?.get_param_from;
  // A field the backend marks as `readonly` is filled in elsewhere, so it is
  // rendered grayed out and cannot be edited.
  const fieldReadOnly = isFlagOn(fieldItem?.readonly ?? fieldItem?.read_only);
  const readOnly = readOnlyProp || fieldReadOnly;
  const acceptableText = useMemo(
    () =>
      isFlagOn(fieldItem?.show_acceptable)
        ? describeAcceptable(parseAcceptable(fieldItem?.acceptable))
        : '',
    [fieldItem?.show_acceptable, fieldItem?.acceptable],
  );

  // When `get_param_from` is set the options (and the `value_url` value) are
  // scoped to the answers of the fields it points at, so they can only be
  // loaded once all are answered.
  const optionsParams = useMemo(
    () =>
      Array.isArray(paramSources)
        ? resolveOptionsParams(paramSources, inspection_id, fields, currentItem)
        : [],
    [paramSources, fields, currentItem, inspection_id],
  );
  const waitingForParam = optionsParams.some(param => param.value === null);
  const optionsUrl = buildOptionsUrl(optionsBaseUrl, optionsParams);

  const {data: optionsData, isFetching: optionsLoading} =
    useGetFieldOptionsQuery(optionsUrl, {
      skip: !optionsUrl || waitingForParam,
    });

  // Fields carrying `value_url` get their answer from that url, which responds
  // with `{data: {value}}`. A completed inspection keeps what was saved.
  const valueUrl = buildOptionsUrl(valueBaseUrl, optionsParams);
  const {data: valueData} = useGetFieldOptionsQuery(valueUrl, {
    skip: !valueUrl || waitingForParam || sheet || data?.is_complete,
  });
  const remoteValue = valueData?.data?.value;

  useEffect(() => {
    if (!valueUrl || remoteValue === null || remoteValue === undefined) {
      return;
    }
    const value = String(remoteValue);
    if (fieldItem?.answers?.[0] === value) {
      return;
    }
    dispatch(
      setFields({
        id: fieldItem?.id,
        answers: [value],
        has_comment: false,
        comment_text: '',
        comment_files: fieldItem?.comment_files,
        data_type: fieldItem?.data_type,
      }),
    );
  }, [remoteValue]);

  const remoteOptions = useMemo(
    () => (optionsBaseUrl ? toRemoteOptions(optionsData) : null),
    [optionsBaseUrl, optionsData],
  );

  const item = useMemo(
    () =>
      remoteOptions?.length
        ? {...fieldItem, options: remoteOptions}
        : fieldItem,
    [fieldItem, remoteOptions],
  );
  const [showOther, setShowOther] = useState(false);
  const fsiiPercentage =
    item?.data_type === 'fsii_percentage'
      ? (fields?.find(f => f?.id === item?.id)?.answers?.[0] ??
        item?.answers?.[0] ??
        '')
      : '';
  const isGravityNumericField = [
    'api_gravity_measured',
    'api_gravity_reported',
    'measured_gravity_minus_reported',
  ].includes(item?.data_type);
  const flowRateGlobal = fields?.filter(
    item => item?.data_type === 'flow_rate',
  )[0];

  // Normalize "Y"/"N" and "Yes"/"No" so radio checked state matches stored value
  const normalizeRadioValue = val => {
    if (val === 'Y' || val === 'Yes' || val === 'yes') return 'Y';
    if (val === 'N' || val === 'No' || val === 'no') return 'N';
    return val;
  };
  const isRadioOptionChecked = (optVal, answer) =>
    optVal === answer ||
    normalizeRadioValue(optVal) === normalizeRadioValue(answer);

  useEffect(() => {
    if (item?.type === 'radio') {
      let data = [];
      currentItem?.fields?.forEach(field => {
        if (field.id === item?.id) {
          field?.options?.forEach(option => {
            if (option?.from_corrective_action) {
              data.push({...option, checked: true});
            } else {
              data.push({...option, checked: false});
            }
          });
        }
      });
      setRadioOptions(data);
    }
  }, [correctiveActionsModal]);

  // Sync radio options from item.answers (e.g. "Y"/"N") so the correct option is checked on load
  useEffect(() => {
    if (item?.type === 'radio' && item?.options?.length) {
      const currentAnswer = item?.answers?.[0];
      setRadioOptions(prev => {
        const fromItem = item.options.map(opt => ({
          ...opt,
          checked: isRadioOptionChecked(opt?.value, currentAnswer),
        }));
        return fromItem.length ? fromItem : prev;
      });
    }
  }, [item?.type, item?.id, item?.answers?.[0], item?.options]);

  // The selected deice tool says which scale it reads on under `brix_ri`, and
  // the reading it does not measure is then hidden. The options are fetched, so
  // this settles once they arrive.
  const toolScale = useMemo(() => {
    if (item?.data_type !== 'deice_tool') {
      return null;
    }
    const answer = item?.answers?.[0];
    if (answer === null || answer === undefined || answer === '') {
      return null;
    }
    return (
      item?.options?.find(option => String(option?.value) === String(answer))
        ?.brix_ri ?? null
    );
  }, [item?.data_type, item?.answers?.[0], item?.options]);

  useEffect(() => {
    if (item?.data_type === 'deice_tool') {
      dispatch(setToolScale(toolScale));
    }
  }, [item?.data_type, toolScale]);

  const renderAcceptable = () => {
    if (!acceptableText) {
      return null;
    }
    const translated = t(ACCEPTABLE_LABEL_KEY);
    const label =
      !translated || translated === ACCEPTABLE_LABEL_KEY
        ? (ACCEPTABLE_LABEL_FALLBACK[language] ?? ACCEPTABLE_LABEL_FALLBACK.en)
        : translated;
    return (
      <span className='fillInspection__field-reference'>
        {label}: {acceptableText}
      </span>
    );
  };

  const formattedText = text => {
    if (text.indexOf('\n') === -1) {
      return text;
    }
    return text.split('\n').map((line, index) => {
      if (index !== 0) {
        return (
          <React.Fragment key={index}>
            {line}
            <br />
          </React.Fragment>
        );
      }
    });
  };

  useEffect(() => {
    if (item?.type === 'sump_sample_optional') {
      if (
        item?.answers.length > 0 &&
        item?.answers[0] !== '1A' &&
        item?.answers[0] !== 'N/A' &&
        item?.answers[0] !== 'N/U'
      ) {
        setShowOther(true);
      } else {
        setShowOther(false);
      }
    }
  }, [item.type]);

  const [fullResult, setFullResult] = useState('0');
  const formCorrectedDP = (observed, actualFlowRate, flowRate) => {
    const s = (Number(actualFlowRate) / Number(flowRate)) * 100;
    const res =
      Number(observed) *
      (2.237 * 2.718 ** (-0.00896 * s) + 9.638 * 2.718 ** (-0.04719 * s));
    setFullResult(String(Math.round(res * 10) / 10));
  };

  const checkFields = () => {
    const observed = fields?.filter(
      item => item?.data_type === 'observed_dp',
    )[0];
    const actualFlowRate = fields?.filter(
      item => item?.data_type === 'actual_flow_rate',
    )[0];
    const flowRate = fields?.filter(item => item?.data_type === 'flow_rate')[0];
    const correctedDp = fields?.filter(
      item => item?.data_type === 'corrected_dp',
    )[0];
    if (
      observed?.answers[0] &&
      actualFlowRate?.answers[0] &&
      flowRate?.answers[0]
    ) {
      formCorrectedDP(
        observed?.answers[0],
        actualFlowRate?.answers[0],
        flowRate?.answers[0],
        correctedDp,
      );
    }
    if (!observed?.answers[0]) {
      toast.error('Observed DP is required for calculation');
    }
    if (!actualFlowRate?.answers[0]) {
      toast.error('Actual Flow Rate is required for calculation');
    }
    if (!flowRate?.answers[0]) {
      toast.error(
        'Flow Rate is required for calculation, change flow rate value in Equipment',
      );
    }
  };

  useEffect(() => {
    if (item?.data_type === 'corrected_dp') {
      const observed = fields?.find(f => f?.data_type === 'observed_dp');
      const actualFlowRate = fields?.find(f => f?.data_type === 'actual_flow_rate');
      const flowRate = fields?.find(f => f?.data_type === 'flow_rate');
      if (observed?.answers[0] && actualFlowRate?.answers[0] && flowRate?.answers[0]) {
        formCorrectedDP(
          observed.answers[0],
          actualFlowRate.answers[0],
          flowRate.answers[0],
        );
      }
    }
  }, []);

  useEffect(() => {
    if (item?.data_type === 'corrected_dp') {
      dispatch(
        setFields({
          id: fields?.filter(item => item?.data_type === 'corrected_dp')[0]?.id,
          answers: [fullResult ? fullResult + '' : '0'],
          has_comment: false,
          comment_text: '',
          comment_files: fields?.filter(
            item => item?.data_type === 'corrected_dp',
          )[0]?.comment_files,
        }),
      );
    }
  }, [fullResult]);

  return (
    <>
      {item?.data_type === 'fsii_percentage' ||
      item?.data_type === 'fsii_condition' ||
      item?.data_type === 'dp_record' ||
      item?.data_type === 'method_corrected_dp' ||
      item?.data_type === 'observed_dp' ||
      item?.data_type === 'actual_flow_rate' ||
      item?.data_type === 'corrected_dp' ? (
        fields?.filter(
          item =>
            item?.data_type === 'fsii_available' ||
            item?.data_type === 'dp_available' ||
            item?.data_type === 'corrected_dp_available',
        )[0]?.answers[0] === 'Y' ? (
          <div className='fillInspection__field'>
            {item?.data_type === 'corrected_dp' &&
            flowRateGlobal?.answers[0] ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingBottom: 10,
                }}
              >
                <p className='fillInspection__field-name'>
                  {formattedText(item?.label)}
                </p>
                <div
                  onClick={() => {
                    checkFields();
                  }}
                  className='header__btn'
                  style={{
                    fontSize: 14,
                    padding: 10,
                    paddingTop: 5,
                    paddingBottom: 5,
                    margin: 0,
                  }}
                >
                  Calculate
                </div>
              </div>
            ) : (
              <p className='fillInspection__field-name'>
                {formattedText(item?.label)}
                {item?.data_type === 'fsii_percentage' && (
                  <Tooltip tooltipText={'Acceptable FSII % is 0.10-0.15%'} />
                )}
                {renderAcceptable()}
              </p>
            )}
            <div className='fillInspection__field-options'>
              <div className='fillInspection__field-option'>
                {item?.type === 'radio' &&
                  item?.options?.map((option, index) => {
                    return (
                      <FillInput
                        readOnly={readOnly}
                        radioOptions={radioOptions}
                        setRadioOptions={setRadioOptions}
                        option={option}
                        key={index}
                        type={'radio'}
                        item={item}
                        disabled={data?.is_complete}
                        index={index}
                        sheet={sheet}
                        inspection_id={inspection_id}
                      />
                    );
                  })}
                {item?.type === 'time' && (
                  <FillTime
                    readOnly={readOnly}
                    disabled={data?.is_complete}
                    item={item}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    category_id={currentItem?.id}
                  />
                )}
                {item?.type === 'photo' && (
                  <FillPhoto
                    readOnly={readOnly}
                    disabled={data?.is_complete}
                    item={item}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    category_id={currentItem?.id}
                    type={'single'}
                  />
                )}
                {item?.type === 'multiple_photo' && (
                  <FillPhoto
                    readOnly={readOnly}
                    disabled={data?.is_complete}
                    item={item}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    category_id={currentItem?.id}
                    type={'multiple'}
                  />
                )}
                {item?.type === 'date' && (
                  <FillDate
                    readOnly={readOnly}
                    disabled={data?.is_complete}
                    item={item}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    category_id={currentItem?.id}
                  />
                )}
                {item?.type === 'checkbox' &&
                  item?.options?.map((option, index) => (
                    <FillInput
                      readOnly={readOnly}
                      disabled={data?.is_complete}
                      radioOptions={[]}
                      setRadioOptions={setRadioOptions}
                      option={option}
                      key={index}
                      type={'checkbox'}
                      item={item}
                      index={index}
                      sheet={sheet}
                      inspection_id={inspection_id}
                    />
                  ))}
                {item?.type === 'select' && (
                  <FillSelect
                    item={item}
                    options={item?.options}
                    isLoading={optionsLoading}
                    sheet={sheet}
                    readOnly={readOnly}
                    disabled={data?.is_complete}
                  />
                )}
                {item?.type === 'numeric' &&
                item?.data_type === 'corrected_dp' ? (
                  <Input
                    type={'number'}
                    disabled={sheet || fieldReadOnly || data?.is_complete}
                    value={Number(fullResult)}
                    containerWidth={'100%'}
                    // readOnly={item?.data_type === 'corrected_dp' ? true : readOnly}
                    onChange={e => {
                      let inputData = {
                        id: item.id,
                        answers: [`${e.target.value}`],
                        has_comment: false,
                        comment_text: '',
                        comment_files: item.comment_files,
                        data_type: item?.data_type,
                      };
                      dispatch(setFields(inputData));
                    }}
                    data_type={item?.data_type}
                  />
                ) : item?.type === 'numeric' ? (
                  <Input
                    type={'number'}
                    value={item?.answers?.[0] ?? ''}
                    disabled={sheet || fieldReadOnly || data?.is_complete}
                    containerWidth={'100%'}
                    // readOnly={item?.data_type === 'corrected_dp' ? true : readOnly}
                    onChange={e => {
                      let inputData = {
                        id: item.id,
                        answers: [`${e.target.value}`],
                        has_comment: false,
                        comment_text: '',
                        comment_files: item.comment_files,
                        data_type: item?.data_type,
                      };
                      dispatch(setFields(inputData));
                    }}
                    data_type={item?.data_type}
                  />
                ) : (
                  <></>
                )}
                {item?.type === 'sump_sample_optional' && (
                  <div className='sump__op'>
                    <div className='sump__option'>
                      <label className='fillInspection__field-radio'>
                        {t('fillInspection.sumpSample.cleanBright')}
                        <input
                          type='radio'
                          value='1A'
                          name={item?.id}
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] === '1A'
                              ? true
                              : false
                          }
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample_optional',
                            };
                            dispatch(setFields(inputData));
                            setShowOther(false);
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillInspection.sumpSample.other')}
                        <input
                          type='radio'
                          value=''
                          name={item?.id}
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== '1A' &&
                            item?.answers[0] !== 'N/A' &&
                            item?.answers[0] !== 'N/U'
                              ? true
                              : false
                          }
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: item?.answers,
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample_optional',
                            };
                            dispatch(setFields(inputData));
                            setShowOther(true);
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillInspection.sumpSample.notApplicable')}
                        <input
                          type='radio'
                          value='N/A'
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] === 'N/A'
                              ? true
                              : false
                          }
                          name={item?.id}
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample_optional',
                            };
                            dispatch(setFields(inputData));
                            setShowOther(false);
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillInspection.sumpSample.notUsed')}
                        <input
                          type='radio'
                          value='N/U'
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] === 'N/U'
                              ? true
                              : false
                          }
                          name={item?.id}
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample_optional',
                            };
                            dispatch(setFields(inputData));
                            setShowOther(false);
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                    </div>
                    {showOther && (
                      <div className='sump__box'>
                        <div className='sump__title'>
                          {t('fillInspection.sumpSample.solids')}
                        </div>
                        <div className='sump'>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.clean')}
                            <input
                              type='radio'
                              value='1'
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              name={item?.id + 'Solids'}
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][0] === '1'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.slight')}
                            <input
                              type='radio'
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][0] === '2'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                              value={'2'}
                              name={item?.id + 'Solids'}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.particulate')}
                            <input
                              type='radio'
                              value={'3'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][0] === '3'
                                  ? true
                                  : false
                              }
                              name={item?.id + 'Solids'}
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.dirty')}
                            <input
                              type='radio'
                              value={'4'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][0] === '4'
                                  ? true
                                  : false
                              }
                              name={item?.id + 'Solids'}
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                        </div>
                      </div>
                    )}
                    {showOther && (
                      <div className='sump__box'>
                        <div className='sump__title'>
                          {t('fillInspection.sumpSample.water')}
                        </div>
                        <div className='sump'>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.bright')}
                            <input
                              type='radio'
                              value={'A'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'A'
                                  ? true
                                  : false
                              }
                              name={item?.id + 'Water'}
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.hazy')}
                            <input
                              type='radio'
                              value={'B'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'B'
                                  ? true
                                  : false
                              }
                              name={item?.id + 'Water'}
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.cloudy')}
                            <input
                              type='radio'
                              value={'C'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'C'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                              name={item?.id + 'Water'}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.wet')}
                            <input
                              type='radio'
                              value={'D'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'D'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                              name={item?.id + 'Water'}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.surfactants')}
                            <input
                              type='radio'
                              value={'E'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'E'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                              name={item?.id + 'Water'}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {item?.type === 'sump_sample' && (
                  <>
                    <div className='sump__box'>
                      <div className='sump__title'>
                        {t('fillInspection.sumpSample.solids')}
                      </div>
                      <div className='sump'>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.clean')}
                          <input
                            type='radio'
                            value='1'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][0] === '1'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.slight')}
                          <input
                            type='radio'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            value={'2'}
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][0] === '2'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.particulate')}
                          <input
                            type='radio'
                            value={'3'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][0] === '3'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.dirty')}
                          <input
                            type='radio'
                            value={'4'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][0] === '4'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                      </div>
                    </div>
                    <div className='sump__box'>
                      <div className='sump__title'>
                        {t('fillInspection.sumpSample.water')}
                      </div>
                      <div className='sump'>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.bright')}
                          <input
                            type='radio'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            value={'A'}
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'A'
                                ? true
                                : false
                            }
                            name={item?.id + 'Water'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.hazy')}
                          <input
                            type='radio'
                            value={'B'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'B'
                                ? true
                                : false
                            }
                            name={item?.id + 'Water'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.cloudy')}
                          <input
                            type='radio'
                            value={'C'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'C'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            name={item?.id + 'Water'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.wet')}
                          <input
                            type='radio'
                            value={'D'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'D'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            name={item?.id + 'Water'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.surfactants')}
                          <input
                            type='radio'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            value={'E'}
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'E'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            name={item?.id + 'Water'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                      </div>
                    </div>
                  </>
                )}
                {item?.type === 'textarea' && (
                  <textarea
                    value={item?.answers?.[0] ?? ''}
                    disabled={
                      readOnly || data?.is_complete
                        ? true
                        : sheet
                          ? true
                          : false
                    }
                    onChange={e => {
                      let inputData = {
                        id: item.id,
                        answers: [`${e.target.value}`],
                        has_comment: false,
                        comment_text: '',
                        comment_files: [],
                      };
                      dispatch(setFields(inputData));
                    }}
                    className='fillInspection__field-textarea'
                  ></textarea>
                )}
                {item?.type !== 'textarea' &&
                  item?.type !== 'radio' &&
                  item?.type !== 'date' &&
                  item?.type !== 'checkbox' &&
                  item?.type !== 'numeric' &&
                  item?.type !== 'select' &&
                  item?.type !== 'sump_sample' &&
                  item?.type !== 'photo' &&
                  item?.type !== 'multiple_photo' &&
                  item?.type !== 'sump_sample_optional' && (
                    <Input
                      type={'text'}
                      value={item?.answers?.[0] ?? ''}
                      disabled={sheet}
                      readOnly={readOnly || data?.is_complete}
                      onChange={e => {
                        let inputData = {
                          id: item.id,
                          answers: [`${e.target.value}`],
                          has_comment: false,
                          comment_text: '',
                          comment_files: item.comment_files,
                        };
                        dispatch(setFields(inputData));
                      }}
                    />
                  )}
              </div>
            </div>
            {item.data_type === 'fsii_percentage' &&
              (fsiiPercentage > -1 || item?.answers[0] > -1) && (
                <div
                  className='fillInspection__field-options'
                  style={{marginTop: 20}}
                >
                  <div className='fillInspection__field-option'>
                    <FillInput
                      readOnly={true}
                      radioOptions={[
                        {
                          checked: data?.is_complete
                            ? item?.answers[0] >= 0.1 &&
                              item?.answers[0] <= 0.15
                            : fsiiPercentage >= 0.1 && fsiiPercentage <= 0.15,
                        },
                        {},
                      ]}
                      setRadioOptions={setRadioOptions}
                      option={{
                        value: 0,
                        label: t('createForm.fields.satisfactory'),
                      }}
                      key={'0'}
                      type={'radio'}
                      item={item}
                      disabled={true}
                      index={0}
                      sheet={sheet}
                      inspection_id={inspection_id}
                      fsiiSelect={true}
                    />
                    <FillInput
                      disabled={true}
                      radioOptions={[
                        {},
                        {
                          checked: data?.is_complete
                            ? item?.answers[0] == 0 ||
                              item?.answers[0] === 0.15 ||
                              item?.answers[0] > 0.15
                              ? true
                              : false
                            : fsiiPercentage == 0 || fsiiPercentage > 0.15
                              ? true
                              : false,
                        },
                      ]}
                      setRadioOptions={setRadioOptions}
                      option={{
                        value: 1,
                        label: t('createNewForm.components.comment'),
                      }}
                      key={'1'}
                      type={'radio'}
                      item={item}
                      index={1}
                      sheet={sheet}
                      inspection_id={inspection_id}
                      fsiiSelect={true}
                    />
                  </div>
                </div>
              )}
            {item.data_type === 'fsii_percentage' &&
              (fsiiPercentage > 0.15 || item?.answers[0] > 0.15) && (
                <div
                  className={'fillInspection__field-options'}
                  style={{marginTop: 20}}
                >
                  <div className={'fillInspection__field-option'}>
                    <Input
                      type={'text'}
                      containerWidth={'100%'}
                      required={true}
                      defaultValue={data?.is_complete ? item?.comment_text : ''}
                      disabled={data?.is_complete || sheet || fieldReadOnly}
                      onChange={e => {
                        let inputData = {
                          id: item.id,
                          answers: [`${fsiiPercentage}`],
                          has_comment: false,
                          comment_text: e.target.value,
                          comment_files: item.comment_files,
                        };
                        dispatch(setFields(inputData));
                      }}
                    />
                  </div>
                </div>
              )}
          </div>
        ) : fields?.filter(
            item =>
              item?.data_type === 'fsii_available' ||
              item?.data_type === 'dp_available' ||
              item?.data_type === 'corrected_dp_available',
          ).length === 0 ? (
          <div className='fillInspection__field'>
            {item?.data_type === 'corrected_dp' &&
            flowRateGlobal?.answers[0] ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingBottom: 10,
                }}
              >
                <p className='fillInspection__field-name'>
                  {formattedText(item?.label)}{' '}
                </p>
                <div
                  onClick={() => {
                    checkFields();
                  }}
                  className='header__top__btn'
                  style={{
                    pointerEvents: calculateDisabled ? 'none' : 'auto',
                    opacity: calculateDisabled ? 0.5 : 1,
                  }}
                >
                  Calculate
                </div>
              </div>
            ) : (
              <p className='fillInspection__field-name'>
                {data?.fuel_receipt_observed_flow_label &&
                item?.data_type === 'actual_flow_rate'
                  ? data?.fuel_receipt_observed_flow_label
                  : formattedText(item?.label)}{' '}
                {item?.data_type === 'fsii_percentage' && (
                  <Tooltip tooltipText={'Acceptable FSII % is 0.10-0.15%'} />
                )}
                {renderAcceptable()}
              </p>
            )}
            <div className='fillInspection__field-options'>
              <div className='fillInspection__field-option'>
                {item?.type === 'radio' &&
                  item?.options?.map((option, index) => {
                    return (
                      <FillInput
                        readOnly={readOnly || data?.is_complete}
                        radioOptions={radioOptions}
                        setRadioOptions={setRadioOptions}
                        option={option}
                        key={index}
                        type={'radio'}
                        item={item}
                        index={index}
                        sheet={sheet}
                        inspection_id={inspection_id}
                      />
                    );
                  })}
                {item?.type === 'time' && (
                  <FillTime
                    readOnly={readOnly}
                    disabled={data?.is_complete}
                    item={item}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    category_id={currentItem?.id}
                  />
                )}
                {item?.type === 'photo' && (
                  <FillPhoto
                    readOnly={readOnly}
                    disabled={data?.is_complete}
                    item={item}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    category_id={currentItem?.id}
                    type={'single'}
                  />
                )}
                {item?.type === 'multiple_photo' && (
                  <FillPhoto
                    readOnly={readOnly}
                    disabled={data?.is_complete}
                    item={item}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    category_id={currentItem?.id}
                    type={'multiple'}
                  />
                )}
                {item?.type === 'date' && (
                  <FillDate
                    readOnly={readOnly}
                    disabled={data?.is_complete}
                    item={item}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    category_id={currentItem?.id}
                  />
                )}
                {item?.type === 'checkbox' &&
                  item?.options?.map((option, index) => (
                    <FillInput
                      readOnly={readOnly || data?.is_complete}
                      radioOptions={[]}
                      setRadioOptions={setRadioOptions}
                      option={option}
                      key={index}
                      type={'checkbox'}
                      item={item}
                      index={index}
                      sheet={sheet}
                      inspection_id={inspection_id}
                    />
                  ))}
                {item?.type === 'select' && (
                  <FillSelect
                    item={item}
                    options={item?.options}
                    isLoading={optionsLoading}
                    sheet={sheet}
                    readOnly={readOnly || data?.is_complete}
                  />
                )}
                {item?.type === 'numeric' &&
                item?.data_type === 'corrected_dp' ? (
                  <>
                    <Input
                      type={'number'}
                      disabled={sheet || fieldReadOnly || data?.is_complete}
                      value={Number(fullResult)}
                      containerWidth={'100%'}
                      // readOnly={item?.data_type === 'corrected_dp' ? true : readOnly}
                      onChange={e => {
                        let inputData = {
                          id: item.id,
                          answers: [`${e.target.value}`],
                          has_comment: false,
                          comment_text: '',
                          comment_files: item.comment_files,
                          data_type: item?.data_type,
                        };
                        dispatch(setFields(inputData));
                      }}
                      data_type={item?.data_type}
                    />
                  </>
                ) : item?.type === 'numeric' ? (
                  <Input
                    type={'number'}
                    value={item?.answers?.[0] ?? ''}
                    disabled={sheet || fieldReadOnly || data?.is_complete}
                    containerWidth={'100%'}
                    // readOnly={item?.data_type === 'corrected_dp' ? true : readOnly}
                    onChange={e => {
                      let inputData = {
                        id: item.id,
                        answers: [`${e.target.value}`],
                        has_comment: false,
                        comment_text: '',
                        comment_files: item.comment_files,
                        data_type: item?.data_type,
                      };
                      dispatch(setFields(inputData));
                    }}
                    data_type={item?.data_type}
                  />
                ) : (
                  <></>
                )}
                {item?.type === 'sump_sample_optional' && (
                  <div className='sump__op'>
                    <div className='sump__option'>
                      <label className='fillInspection__field-radio'>
                        {t('fillInspection.sumpSample.cleanBright')}
                        <input
                          type='radio'
                          value='1A'
                          name={item?.id}
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] === '1A'
                              ? true
                              : false
                          }
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample_optional',
                            };
                            dispatch(setFields(inputData));
                            setShowOther(false);
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillInspection.sumpSample.other')}
                        <input
                          type='radio'
                          value=''
                          name={item?.id}
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== '1A' &&
                            item?.answers[0] !== 'N/A' &&
                            item?.answers[0] !== 'N/U'
                              ? true
                              : false
                          }
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: item?.answers,
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample_optional',
                            };
                            dispatch(setFields(inputData));
                            setShowOther(true);
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillInspection.sumpSample.notApplicable')}
                        <input
                          type='radio'
                          value='N/A'
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] === 'N/A'
                              ? true
                              : false
                          }
                          name={item?.id}
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample_optional',
                            };
                            dispatch(setFields(inputData));
                            setShowOther(false);
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillInspection.sumpSample.notUsed')}
                        <input
                          type='radio'
                          value='N/U'
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] === 'N/U'
                              ? true
                              : false
                          }
                          name={item?.id}
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample_optional',
                            };
                            dispatch(setFields(inputData));
                            setShowOther(false);
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                    </div>
                    {showOther && (
                      <div className='sump__box'>
                        <div className='sump__title'>
                          {t('fillInspection.sumpSample.solids')}
                        </div>
                        <div className='sump'>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.clean')}
                            <input
                              type='radio'
                              value='1'
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              name={item?.id + 'Solids'}
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][0] === '1'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.slight')}
                            <input
                              type='radio'
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][0] === '2'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                              value={'2'}
                              name={item?.id + 'Solids'}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.particulate')}
                            <input
                              type='radio'
                              value={'3'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][0] === '3'
                                  ? true
                                  : false
                              }
                              name={item?.id + 'Solids'}
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.dirty')}
                            <input
                              type='radio'
                              value={'4'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][0] === '4'
                                  ? true
                                  : false
                              }
                              name={item?.id + 'Solids'}
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                        </div>
                      </div>
                    )}
                    {showOther && (
                      <div className='sump__box'>
                        <div className='sump__title'>
                          {t('fillInspection.sumpSample.water')}
                        </div>
                        <div className='sump'>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.bright')}
                            <input
                              type='radio'
                              value={'A'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'A'
                                  ? true
                                  : false
                              }
                              name={item?.id + 'Water'}
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.hazy')}
                            <input
                              type='radio'
                              value={'B'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'B'
                                  ? true
                                  : false
                              }
                              name={item?.id + 'Water'}
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.cloudy')}
                            <input
                              type='radio'
                              value={'C'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'C'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                              name={item?.id + 'Water'}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.wet')}
                            <input
                              type='radio'
                              value={'D'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'D'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                              name={item?.id + 'Water'}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                          <label className='fillInspection__field-radio'>
                            {t('fillInspection.sumpSample.surfactants')}
                            <input
                              type='radio'
                              value={'E'}
                              disabled={
                                readOnly || data?.is_complete
                                  ? true
                                  : sheet
                                    ? true
                                    : false
                              }
                              defaultChecked={
                                item?.answers.length > 0 &&
                                item?.answers[0][1] === 'E'
                                  ? true
                                  : false
                              }
                              onChange={e => {
                                let inputData = {
                                  id: item.id,
                                  answers: [`${e.target.value}`],
                                  has_comment: false,
                                  comment_text: '',
                                  comment_files: item.comment_files,
                                  type: 'sump_sample',
                                };
                                dispatch(setFields(inputData));
                              }}
                              name={item?.id + 'Water'}
                            />
                            <span className={`checkmark`}></span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {item?.type === 'sump_sample' && (
                  <>
                    <div className='sump__box'>
                      <div className='sump__title'>
                        {t('fillInspection.sumpSample.solids')}
                      </div>
                      <div className='sump'>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.clean')}
                          <input
                            type='radio'
                            value='1'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][0] === '1'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.slight')}
                          <input
                            type='radio'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            value={'2'}
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][0] === '2'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.particulate')}
                          <input
                            type='radio'
                            value={'3'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][0] === '3'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.dirty')}
                          <input
                            type='radio'
                            value={'4'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][0] === '4'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                      </div>
                    </div>
                    <div className='sump__box'>
                      <div className='sump__title'>
                        {t('fillInspection.sumpSample.water')}
                      </div>
                      <div className='sump'>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.bright')}
                          <input
                            type='radio'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            value={'A'}
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'A'
                                ? true
                                : false
                            }
                            name={item?.id + 'Water'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.hazy')}
                          <input
                            type='radio'
                            value={'B'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'B'
                                ? true
                                : false
                            }
                            name={item?.id + 'Water'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.cloudy')}
                          <input
                            type='radio'
                            value={'C'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'C'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            name={item?.id + 'Water'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.wet')}
                          <input
                            type='radio'
                            value={'D'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'D'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            name={item?.id + 'Water'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.surfactants')}
                          <input
                            type='radio'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            value={'E'}
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0][1] === 'E'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            name={item?.id + 'Water'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                      </div>
                    </div>
                  </>
                )}
                {item?.type === 'textarea' && (
                  <textarea
                    value={item?.answers?.[0] ?? ''}
                    disabled={
                      readOnly || data?.is_complete
                        ? true
                        : sheet
                          ? true
                          : false
                    }
                    onChange={e => {
                      let inputData = {
                        id: item.id,
                        answers: [`${e.target.value}`],
                        has_comment: false,
                        comment_text: '',
                        comment_files: [],
                      };
                      dispatch(setFields(inputData));
                    }}
                    className='fillInspection__field-textarea'
                  ></textarea>
                )}
                {item?.type !== 'textarea' &&
                  item?.type !== 'radio' &&
                  item?.type !== 'date' &&
                  item?.type !== 'checkbox' &&
                  item?.type !== 'numeric' &&
                  item?.type !== 'select' &&
                  item?.type !== 'sump_sample' &&
                  item?.type !== 'photo' &&
                  item?.type !== 'multiple_photo' &&
                  item?.type !== 'sump_sample_optional' && (
                    <Input
                      type={'text'}
                      value={item?.answers?.[0] ?? ''}
                      disabled={sheet}
                      readOnly={readOnly || data?.is_complete}
                      onChange={e => {
                        let inputData = {
                          id: item.id,
                          answers: [`${e.target.value}`],
                          has_comment: false,
                          comment_text: '',
                          comment_files: item.comment_files,
                        };
                        dispatch(setFields(inputData));
                      }}
                    />
                  )}
              </div>
            </div>
            {item.data_type === 'fsii_percentage' &&
              (fsiiPercentage > -1 || item?.answers[0] > -1) && (
                <div
                  className='fillInspection__field-options'
                  style={{marginTop: 20}}
                >
                  <div className='fillInspection__field-option'>
                    <FillInput
                      readOnly={true}
                      radioOptions={[
                        {
                          checked: data?.is_complete
                            ? item?.answers[0] >= 0.1 &&
                              item?.answers[0] <= 0.15
                            : fsiiPercentage >= 0.1 && fsiiPercentage <= 0.15,
                        },
                        {},
                      ]}
                      setRadioOptions={setRadioOptions}
                      option={{
                        value: 0,
                        label: t('createForm.fields.satisfactory'),
                      }}
                      key={'0'}
                      type={'radio'}
                      item={item}
                      disabled={true}
                      index={0}
                      sheet={sheet}
                      inspection_id={inspection_id}
                      fsiiSelect={true}
                    />
                    <FillInput
                      disabled={true}
                      radioOptions={[
                        {},
                        {
                          checked: data?.is_complete
                            ? item?.answers[0] <= 0 ||
                              item?.answers[0] === 0.15 ||
                              item?.answers[0] > 0.15 ||
                              parseFloat(+item?.answers[0]) < 0.1
                              ? true
                              : false
                            : fsiiPercentage == 0 ||
                                fsiiPercentage > 0.15 ||
                                parseFloat(+fsiiPercentage) < 0.1
                              ? true
                              : false,
                        },
                      ]}
                      setRadioOptions={setRadioOptions}
                      option={{
                        value: 1,
                        label: t('createNewForm.components.comment'),
                      }}
                      key={'1'}
                      type={'radio'}
                      item={item}
                      index={1}
                      sheet={sheet}
                      inspection_id={inspection_id}
                      fsiiSelect={true}
                    />
                  </div>
                </div>
              )}
            {item.data_type === 'fsii_percentage' &&
              (fsiiPercentage > 0.15 ||
                item?.answers[0] > 0.15 ||
                parseFloat(+fsiiPercentage) < 0.1) && (
                <div
                  className={'fillInspection__field-options'}
                  style={{marginTop: 20}}
                >
                  <div className={'fillInspection__field-option'}>
                    <Input
                      type={'text'}
                      containerWidth={'100%'}
                      required={true}
                      defaultValue={data?.is_complete ? item?.comment_text : ''}
                      disabled={data?.is_complete || sheet || fieldReadOnly}
                      onChange={e => {
                        let inputData = {
                          id: item.id,
                          answers: [`${fsiiPercentage}`],
                          has_comment: false,
                          comment_text: e.target.value,
                          comment_files: item.comment_files,
                        };
                        dispatch(setFields(inputData));
                      }}
                    />
                  </div>
                </div>
              )}
          </div>
        ) : null
      ) : (
        <div className='fillInspection__field'>
          {item?.data_type === 'corrected_dp' && flowRateGlobal?.answers[0] ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingBottom: 10,
              }}
            >
              <p className='fillInspection__field-name'>
                {formattedText(item?.label)}{' '}
              </p>
              <div
                onClick={() => {
                  checkFields();
                }}
                className='header__btn'
                style={{
                  fontSize: 14,
                  padding: 10,
                  paddingTop: 5,
                  paddingBottom: 5,
                  margin: 0,
                }}
              >
                Calculate
              </div>
            </div>
          ) : (
            <p className='fillInspection__field-name'>
              {formattedText(item?.label)}
              {item?.tooltip ? <Tooltip tooltipText={item?.tooltip} /> : null}
              {renderAcceptable()}
            </p>
          )}
          <div className='fillInspection__field-options'>
            <div className='fillInspection__field-option'>
              {item?.type === 'radio' &&
                item?.options?.map((option, index) => {
                  return (
                    <FillInput
                      readOnly={readOnly || data?.is_complete}
                      radioOptions={radioOptions}
                      setRadioOptions={setRadioOptions}
                      option={option}
                      key={index}
                      type={'radio'}
                      item={item}
                      index={index}
                      sheet={sheet}
                      inspection_id={inspection_id}
                      currentItem={currentItem}
                      setCurrentItem={setCurrentItem}
                      correctiveActionsModal={correctiveActionsModal}
                    />
                  );
                })}
              {item?.type === 'time' && (
                <FillTime
                  readOnly={readOnly}
                  disabled={data?.is_complete}
                  item={item}
                  sheet={sheet}
                  inspection_id={inspection_id}
                  category_id={currentItem?.id}
                />
              )}
              {item?.type === 'photo' && (
                <FillPhoto
                  readOnly={readOnly}
                  disabled={data?.is_complete}
                  item={item}
                  sheet={sheet}
                  inspection_id={inspection_id}
                  category_id={currentItem?.id}
                  type={'single'}
                />
              )}
              {item?.type === 'multiple_photo' && (
                <FillPhoto
                  readOnly={readOnly}
                  disabled={data?.is_complete}
                  item={item}
                  sheet={sheet}
                  inspection_id={inspection_id}
                  category_id={currentItem?.id}
                  type={'multiple'}
                />
              )}
              {item?.type === 'date' && (
                <FillDate
                  readOnly={readOnly}
                  disabled={data?.is_complete}
                  item={item}
                  sheet={sheet}
                  inspection_id={inspection_id}
                  category_id={currentItem?.id}
                />
              )}
              {item?.type === 'checkbox' &&
                item?.options?.map((option, index) => (
                  <FillInput
                    readOnly={readOnly || data?.is_complete}
                    radioOptions={[]}
                    setRadioOptions={setRadioOptions}
                    option={option}
                    key={index}
                    type={'checkbox'}
                    item={item}
                    index={index}
                    sheet={sheet}
                    inspection_id={inspection_id}
                  />
                ))}
              {item?.type === 'select' && (
                <FillSelect
                  item={item}
                  options={item?.options}
                  isLoading={optionsLoading}
                  sheet={sheet}
                  readOnly={readOnly || data?.is_complete}
                />
              )}
              {item?.type === 'numeric' &&
              item?.data_type === 'corrected_dp' ? (
                <>
                  <Input
                    type={'number'}
                    disabled={sheet || fieldReadOnly || data?.is_complete}
                    value={Number(fullResult)}
                    containerWidth={'100%'}
                    // readOnly={item?.data_type === 'corrected_dp' ? true : readOnly}
                    onChange={e => {
                      let inputData = {
                        id: item.id,
                        answers: [`${e.target.value}`],
                        has_comment: false,
                        comment_text: '',
                        comment_files: item.comment_files,
                      };
                      dispatch(setFields(inputData));
                    }}
                    data_type={item?.data_type}
                  />
                </>
              ) : item?.type === 'numeric' ? (
                <Input
                  type={'number'}
                  value={
                    item?.data_type === 'fsii_percentage'
                      ? fsiiPercentage
                      : (item?.answers?.[0] ?? '')
                  }
                  disabled={
                    sheet ||
                    data?.is_complete ||
                    item?.data_type === 'measured_gravity_minus_reported' ||
                    item?.data_type === 'calculated_ullage' ||
                    item?.data_type === 'lout'
                  }
                  containerWidth={'100%'}
                  // readOnly={item?.data_type === 'corrected_dp' ? true : readOnly}
                  onChange={e => {
                    let inputData = {
                      id: item.id,
                      answers: [`${e.target.value}`],
                      has_comment: false,
                      comment_text: '',
                      comment_files: item.comment_files,
                      data_type: item?.data_type,
                    };
                    dispatch(setFields(inputData));
                  }}
                  data_type={item?.data_type}
                />
              ) : item?.type === 'dry_rating' ? (
                <FillDryRating
                  item={item}
                  readOnly={readOnly || data?.is_complete}
                />
              ) : (
                <></>
              )}
              {item?.type === 'sump_sample_optional' && (
                <div className='sump__op'>
                  <div className='sump__option'>
                    <label className='fillInspection__field-radio'>
                      {t('fillInspection.sumpSample.cleanBright')}
                      <input
                        type='radio'
                        value='1A'
                        name={item?.id}
                        defaultChecked={
                          item?.answers.length > 0 && item?.answers[0] === '1A'
                            ? true
                            : false
                        }
                        disabled={
                          readOnly || data?.is_complete
                            ? true
                            : sheet
                              ? true
                              : false
                        }
                        onChange={e => {
                          let inputData = {
                            id: item.id,
                            answers: [`${e.target.value}`],
                            has_comment: false,
                            comment_text: '',
                            comment_files: item.comment_files,
                            type: 'sump_sample_optional',
                          };
                          dispatch(setFields(inputData));
                          setShowOther(false);
                        }}
                      />
                      <span className={`checkmark`}></span>
                    </label>
                    <label className='fillInspection__field-radio'>
                      {t('fillInspection.sumpSample.other')}
                      <input
                        type='radio'
                        value=''
                        name={item?.id}
                        disabled={
                          readOnly || data?.is_complete
                            ? true
                            : sheet
                              ? true
                              : false
                        }
                        defaultChecked={
                          item?.answers.length > 0 &&
                          item?.answers[0] !== '1A' &&
                          item?.answers[0] !== 'N/A' &&
                          item?.answers[0] !== 'N/U'
                            ? true
                            : false
                        }
                        onChange={e => {
                          let inputData = {
                            id: item.id,
                            answers: item?.answers,
                            has_comment: false,
                            comment_text: '',
                            comment_files: item.comment_files,
                            type: 'sump_sample_optional',
                          };
                          dispatch(setFields(inputData));
                          setShowOther(true);
                        }}
                      />
                      <span className={`checkmark`}></span>
                    </label>
                    <label className='fillInspection__field-radio'>
                      {t('fillInspection.sumpSample.notApplicable')}
                      <input
                        type='radio'
                        value='N/A'
                        disabled={
                          readOnly || data?.is_complete
                            ? true
                            : sheet
                              ? true
                              : false
                        }
                        defaultChecked={
                          item?.answers.length > 0 && item?.answers[0] === 'N/A'
                            ? true
                            : false
                        }
                        name={item?.id}
                        onChange={e => {
                          let inputData = {
                            id: item.id,
                            answers: [`${e.target.value}`],
                            has_comment: false,
                            comment_text: '',
                            comment_files: item.comment_files,
                            type: 'sump_sample_optional',
                          };
                          dispatch(setFields(inputData));
                          setShowOther(false);
                        }}
                      />
                      <span className={`checkmark`}></span>
                    </label>
                    <label className='fillInspection__field-radio'>
                      {t('fillInspection.sumpSample.notUsed')}
                      <input
                        type='radio'
                        value='N/U'
                        disabled={
                          readOnly || data?.is_complete
                            ? true
                            : sheet
                              ? true
                              : false
                        }
                        defaultChecked={
                          item?.answers.length > 0 && item?.answers[0] === 'N/U'
                            ? true
                            : false
                        }
                        name={item?.id}
                        onChange={e => {
                          let inputData = {
                            id: item.id,
                            answers: [`${e.target.value}`],
                            has_comment: false,
                            comment_text: '',
                            comment_files: item.comment_files,
                            type: 'sump_sample_optional',
                          };
                          dispatch(setFields(inputData));
                          setShowOther(false);
                        }}
                      />
                      <span className={`checkmark`}></span>
                    </label>
                  </div>
                  {showOther && (
                    <div className='sump__box'>
                      <div className='sump__title'>
                        {t('fillInspection.sumpSample.solids')}
                      </div>
                      <div className='sump'>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.clean')}
                          <input
                            type='radio'
                            value='1'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            name={item?.id + 'Solids'}
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0] !== null &&
                              item?.answers[0][0] === '1'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.slight')}
                          <input
                            type='radio'
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0] !== null &&
                              item?.answers[0][0] === '2'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            value={'2'}
                            name={item?.id + 'Solids'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.particulate')}
                          <input
                            type='radio'
                            value={'3'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0] !== null &&
                              item?.answers[0][0] === '3'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.dirty')}
                          <input
                            type='radio'
                            value={'4'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0] !== null &&
                              item?.answers[0][0] === '4'
                                ? true
                                : false
                            }
                            name={item?.id + 'Solids'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                      </div>
                    </div>
                  )}
                  {showOther && (
                    <div className='sump__box'>
                      <div className='sump__title'>
                        {t('fillInspection.sumpSample.water')}
                      </div>
                      <div className='sump'>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.bright')}
                          <input
                            type='radio'
                            value={'A'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0] !== null &&
                              item?.answers[0][1] === 'A'
                                ? true
                                : false
                            }
                            name={item?.id + 'Water'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.hazy')}
                          <input
                            type='radio'
                            value={'B'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0] !== null &&
                              item?.answers[0][1] === 'B'
                                ? true
                                : false
                            }
                            name={item?.id + 'Water'}
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.cloudy')}
                          <input
                            type='radio'
                            value={'C'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0] !== null &&
                              item?.answers[0][1] === 'C'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            name={item?.id + 'Water'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.wet')}
                          <input
                            type='radio'
                            value={'D'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0] !== null &&
                              item?.answers[0][1] === 'D'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            name={item?.id + 'Water'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                        <label className='fillInspection__field-radio'>
                          {t('fillInspection.sumpSample.surfactants')}
                          <input
                            type='radio'
                            value={'E'}
                            disabled={
                              readOnly || data?.is_complete
                                ? true
                                : sheet
                                  ? true
                                  : false
                            }
                            defaultChecked={
                              item?.answers.length > 0 &&
                              item?.answers[0] !== null &&
                              item?.answers[0][1] === 'E'
                                ? true
                                : false
                            }
                            onChange={e => {
                              let inputData = {
                                id: item.id,
                                answers: [`${e.target.value}`],
                                has_comment: false,
                                comment_text: '',
                                comment_files: item.comment_files,
                                type: 'sump_sample',
                              };
                              dispatch(setFields(inputData));
                            }}
                            name={item?.id + 'Water'}
                          />
                          <span className={`checkmark`}></span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {item?.type === 'sump_sample' && (
                <>
                  <div className='sump__box'>
                    <div className='sump__title'>{t('fillField.solids')}</div>
                    <div className='sump'>
                      <label className='fillInspection__field-radio'>
                        {t('fillField.ratings.solid.clean')}
                        <input
                          type='radio'
                          value='1'
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== null &&
                            item?.answers[0][0] === '1'
                              ? true
                              : false
                          }
                          name={item?.id + 'Solids'}
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample',
                            };
                            dispatch(setFields(inputData));
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillField.ratings.solid.slight')}
                        <input
                          type='radio'
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample',
                            };
                            dispatch(setFields(inputData));
                          }}
                          value={'2'}
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== null &&
                            item?.answers[0][0] === '2'
                              ? true
                              : false
                          }
                          name={item?.id + 'Solids'}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillField.ratings.solid.particulate')}
                        <input
                          type='radio'
                          value={'3'}
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== null &&
                            item?.answers[0][0] === '3'
                              ? true
                              : false
                          }
                          name={item?.id + 'Solids'}
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample',
                            };
                            dispatch(setFields(inputData));
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillField.ratings.solid.dirty')}
                        <input
                          type='radio'
                          value={'4'}
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== null &&
                            item?.answers[0][0] === '4'
                              ? true
                              : false
                          }
                          name={item?.id + 'Solids'}
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample',
                            };
                            dispatch(setFields(inputData));
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                    </div>
                  </div>
                  <div className='sump__box'>
                    <div className='sump__title'>{t('fillField.water')}</div>
                    <div className='sump'>
                      <label className='fillInspection__field-radio'>
                        {t('fillField.ratings.water.bright')}
                        <input
                          type='radio'
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          value={'A'}
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== null &&
                            item?.answers[0][1] === 'A'
                              ? true
                              : false
                          }
                          name={item?.id + 'Water'}
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample',
                            };
                            dispatch(setFields(inputData));
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillField.ratings.water.hazy')}
                        <input
                          type='radio'
                          value={'B'}
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== null &&
                            item?.answers[0][1] === 'B'
                              ? true
                              : false
                          }
                          name={item?.id + 'Water'}
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample',
                            };
                            dispatch(setFields(inputData));
                          }}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillField.ratings.water.cloudy')}
                        <input
                          type='radio'
                          value={'C'}
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== null &&
                            item?.answers[0][1] === 'C'
                              ? true
                              : false
                          }
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample',
                            };
                            dispatch(setFields(inputData));
                          }}
                          name={item?.id + 'Water'}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillField.ratings.water.wet')}
                        <input
                          type='radio'
                          value={'D'}
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== null &&
                            item?.answers[0][1] === 'D'
                              ? true
                              : false
                          }
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample',
                            };
                            dispatch(setFields(inputData));
                          }}
                          name={item?.id + 'Water'}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                      <label className='fillInspection__field-radio'>
                        {t('fillField.ratings.water.surfactants')}
                        <input
                          type='radio'
                          disabled={
                            readOnly || data?.is_complete
                              ? true
                              : sheet
                                ? true
                                : false
                          }
                          value={'E'}
                          defaultChecked={
                            item?.answers.length > 0 &&
                            item?.answers[0] !== null &&
                            item?.answers[0][1] === 'E'
                              ? true
                              : false
                          }
                          onChange={e => {
                            let inputData = {
                              id: item.id,
                              answers: [`${e.target.value}`],
                              has_comment: false,
                              comment_text: '',
                              comment_files: item.comment_files,
                              type: 'sump_sample',
                            };
                            dispatch(setFields(inputData));
                          }}
                          name={item?.id + 'Water'}
                        />
                        <span className={`checkmark`}></span>
                      </label>
                    </div>
                  </div>
                </>
              )}
              {item?.type === 'textarea' && (
                <textarea
                  value={item?.answers?.[0] ?? ''}
                  disabled={
                    readOnly || data?.is_complete ? true : sheet ? true : false
                  }
                  onChange={e => {
                    let inputData = {
                      id: item.id,
                      answers: [`${e.target.value}`],
                      has_comment: false,
                      comment_text: '',
                      comment_files: [],
                    };
                    dispatch(setFields(inputData));
                  }}
                  className='fillInspection__field-textarea'
                ></textarea>
              )}
              {item?.type !== 'textarea' &&
                item?.type !== 'radio' &&
                item?.type !== 'date' &&
                item?.type !== 'checkbox' &&
                item?.type !== 'numeric' &&
                item?.type !== 'select' &&
                item?.type !== 'sump_sample' &&
                item?.type !== 'photo' &&
                item?.type !== 'multiple_photo' &&
                item?.type !== 'dry_rating' &&
                item?.type !== 'sump_sample_optional' &&
                item?.type !== 'time' && (
                  <textarea
                    value={item?.answers?.[0] ?? ''}
                    disabled={sheet || readOnly || data?.is_complete}
                    readOnly={readOnly || data?.is_complete}
                    onChange={e => {
                      let inputData = {
                        id: item.id,
                        answers: [`${e.target.value}`],
                        has_comment: false,
                        comment_text: '',
                        comment_files: item.comment_files,
                      };
                      dispatch(setFields(inputData));
                    }}
                    className='fillInspection__field-textarea'
                    style={{
                      width: '100%',
                      maxWidth: '90%',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      overflowY: 'auto',
                      maxHeight: '150px',
                      resize: 'none',
                      color:
                        sheet || readOnly || data?.is_complete
                          ? '#999999'
                          : undefined,
                    }}
                  />
                )}
            </div>
          </div>
          {item.data_type === 'fsii_percentage' &&
            (fsiiPercentage > -1 || item?.answers[0] > -1) && (
              <div
                className='fillInspection__field-options'
                style={{marginTop: 20}}
              >
                <div className='fillInspection__field-option'>
                  <FillInput
                    readOnly={true}
                    radioOptions={[
                      {
                        checked: data?.is_complete
                          ? item?.answers[0] >= 0.1 && item?.answers[0] <= 0.15
                          : fsiiPercentage >= 0.1 && fsiiPercentage <= 0.15,
                      },
                      {},
                    ]}
                    setRadioOptions={setRadioOptions}
                    option={{
                      value: 0,
                      label: t('createForm.fields.satisfactory'),
                    }}
                    key={'0'}
                    type={'radio'}
                    item={item}
                    disabled={true}
                    index={0}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    fsiiSelect={true}
                  />
                  <FillInput
                    readOnly={true}
                    radioOptions={[
                      {},
                      {
                        checked: data?.is_complete
                          ? item?.answers[0] == 0 ||
                            item?.answers[0] === 0.15 ||
                            item?.answers[0] > 0.15
                            ? true
                            : false
                          : fsiiPercentage == 0 || fsiiPercentage > 0.15
                            ? true
                            : false,
                      },
                    ]}
                    setRadioOptions={setRadioOptions}
                    option={{
                      value: 1,
                      label: t('createNewForm.components.comment'),
                    }}
                    key={'1'}
                    type={'radio'}
                    item={item}
                    disabled={true}
                    index={1}
                    sheet={sheet}
                    inspection_id={inspection_id}
                    fsiiSelect={true}
                  />
                </div>
              </div>
            )}
          {item.data_type === 'fsii_percentage' &&
            (fsiiPercentage > 0.15 || item?.answers[0] > 0.15) && (
              <div
                className={'fillInspection__field-options'}
                style={{marginTop: 20}}
              >
                <div className={'fillInspection__field-option'}>
                  <Input
                    type={'text'}
                    containerWidth={'100%'}
                    required={true}
                    defaultValue={data?.is_complete ? item?.comment_text : ''}
                    disabled={data?.is_complete || sheet || fieldReadOnly}
                    onChange={e => {
                      let inputData = {
                        id: item.id,
                        answers: [`${fsiiPercentage}`],
                        has_comment: false,
                        comment_text: e.target.value,
                        comment_files: item.comment_files,
                      };
                      dispatch(setFields(inputData));
                    }}
                  />
                </div>
              </div>
            )}
        </div>
      )}
    </>
  );
}
